// Arlo's firsts: the little wins worth a cheer, spotted from what you log.
// Pure. Only counts what happened after he came home, and only once each.
//
// The night one is careful not to cheer for a night nobody logged: it needs
// something logged in the evening before *and* a wee the next morning, with
// no toilet trip in between.

import { aloneSessions } from './alone.ts';
import { DAY, HOUR, MINUTE, addDays, addMonths, atClock, dayStart, parseISODate } from './time.ts';
import type { Household, ISODate, LogEvent, Millis } from './types.ts';

export type FirstIcon = 'home' | 'drop' | 'sun' | 'moon' | 'paw' | 'star' | 'heart' | 'trophy' | 'cake';

export interface FirstDef {
  id: string;
  title: string;
  /** Said when it happens. */
  cheer: string;
  /** Shown while it's still to come. */
  hint: string;
  icon: FirstIcon;
}

export interface First extends FirstDef {
  at: Millis;
}

export const FIRSTS: FirstDef[] = [
  { id: 'home', title: 'Home for good', cheer: 'Day one of the rest of his life with you.', hint: 'The day he comes home.', icon: 'home' },
  { id: 'wee-outside', title: 'First wee outside', cheer: 'The first of thousands. Big praise every single time.', hint: 'Log a wee outside.', icon: 'drop' },
  { id: 'poo-outside', title: 'First poo outside', cheer: 'Right where it belongs. Treat, praise, a little party.', hint: 'Log a poo outside.', icon: 'drop' },
  { id: 'carried-out', title: 'First trip out into the world', cheer: 'Sights, sounds and smells from the safety of your arms.', hint: 'Log a carried outing.', icon: 'star' },
  { id: 'clean-day', title: 'First accident-free day', cheer: 'A whole day with every wee and poo outside. Brilliant teamwork.', hint: 'A full day with every toilet trip outside.', icon: 'sun' },
  { id: 'quiet-night', title: 'First night with no toilet trip', cheer: 'He held it all night. Sleep is back on the menu.', hint: 'A night with no trips logged between bedtime and morning.', icon: 'moon' },
  { id: 'ten-in-a-row', title: 'Ten outside in a row', cheer: 'Ten toilet trips in a row, all outside. It’s clicking.', hint: 'Ten wees and poos in a row, all outside.', icon: 'trophy' },
  { id: 'settled-alone', title: 'First time settled on his own', cheer: 'He settled while you were out. That’s the hardest lesson there is.', hint: 'Alone time where he settles.', icon: 'heart' },
  { id: 'dog-friend', title: 'First happy hello with a dog', cheer: 'A calm, happy meeting with another dog. Lovely.', hint: 'Log a happy reaction to another dog.', icon: 'paw' },
  { id: 'first-walk', title: 'First walk on his own four paws', cheer: 'Out on the pavement, nose down, tail up.', hint: 'Log a walk (not carried) once the vet says he can go out.', icon: 'paw' },
  { id: 'week-home', title: 'One week home', cheer: 'A whole week. Look how far you’ve all come.', hint: 'Seven days after he comes home.', icon: 'cake' },
  { id: 'hundred-outside', title: '100 trips outside', cheer: 'One hundred wees and poos outside. A proper milestone.', hint: '100 wees and poos outside.', icon: 'trophy' },
  { id: 'month-home', title: 'One month home', cheer: 'A month of Arlo. Time for a photo next to the first one.', hint: 'A month after he comes home.', icon: 'cake' },
  { id: 'hundred-days', title: '100 days home', cheer: 'One hundred days together.', hint: '100 days after he comes home.', icon: 'cake' },
];

const DOG_ITEMS = new Set(['calm-adult-dog', 'puppy-class', 'puppy-friend', 'dogs-passing', 'big-dogs']);
const isToilet = (e: LogEvent): e is LogEvent<'wee' | 'poo'> => e.type === 'wee' || e.type === 'poo';
const outside = (e: LogEvent<'wee' | 'poo'>) => e.data.where === 'outside';

/** The firsts reached so far, in the order they happened. */
export function firsts(events: LogEvent[], homeDate: ISODate | undefined, household: Pick<Household, 'nightStart' | 'nightEnd'>, now: Millis, arrivedAt?: Millis): First[] {
  if (!homeDate) return [];
  const home = parseISODate(homeDate).getTime();
  if (now < home) return [];
  const evs = events.filter((e) => e.at >= home && e.at <= now).sort((a, b) => a.at - b.at);
  const found = new Map<string, Millis>();
  const mark = (id: string, at: Millis | undefined) => {
    if (at !== undefined && !found.has(id)) found.set(id, at);
  };

  // The moment you tapped "He's home!", if you did (and it was that day or after).
  mark('home', arrivedAt !== undefined && arrivedAt >= home && arrivedAt <= now ? arrivedAt : home);
  for (const [id, date] of [
    ['week-home', addDays(homeDate, 7)],
    ['month-home', addMonths(homeDate, 1)],
    ['hundred-days', addDays(homeDate, 100)],
  ] as const) {
    const t = parseISODate(date).getTime();
    if (t <= now) mark(id, t);
  }

  const toilet = evs.filter(isToilet);
  mark('wee-outside', toilet.find((e) => e.type === 'wee' && outside(e))?.at);
  mark('poo-outside', toilet.find((e) => e.type === 'poo' && outside(e))?.at);

  let run = 0;
  let total = 0;
  for (const e of toilet) {
    if (outside(e)) {
      run++;
      total++;
      if (run === 10) mark('ten-in-a-row', e.at);
      if (total === 100) mark('hundred-outside', e.at);
    } else run = 0;
  }

  // A finished day with at least four toilet trips logged, all outside.
  const byDay = new Map<number, LogEvent<'wee' | 'poo'>[]>();
  for (const e of toilet) {
    const d = dayStart(e.at);
    byDay.set(d, [...(byDay.get(d) ?? []), e]);
  }
  for (const [d, list] of [...byDay].sort((a, b) => a[0] - b[0])) {
    if (dayStart(d, 1) > now) break;
    if (list.length >= 4 && list.every(outside)) {
      mark('clean-day', list[list.length - 1].at);
      break;
    }
  }

  // Nights: something logged by hand in the three hours before bedtime, a
  // morning wee (from half an hour before the night ends — he'll often wake
  // a little early), and no toilet trip between bedtime and that wee.
  // One pass with moving indexes: this runs every minute on the kiosk.
  const manual = evs.filter((e) => e.type !== 'camera' && e.type !== 'photo' && e.type !== 'tracker' && e.source !== 'automation');
  const wees = toilet.filter((e) => e.type === 'wee');
  let mi = 0;
  let ti = 0;
  let wi = 0;
  for (let d = home; d < now && !found.has('quiet-night'); d = dayStart(d, 1)) {
    const start = atClock(d, household.nightStart);
    let end = atClock(d, household.nightEnd);
    if (end <= start) end = atClock(dayStart(d, 1), household.nightEnd);
    if (end > now) break;
    if (start < home) continue;
    while (mi < manual.length && manual[mi].at < start - 3 * HOUR) mi++;
    const evening = mi < manual.length && manual[mi].at < start;
    while (wi < wees.length && wees[wi].at < end - 30 * MINUTE) wi++;
    const morning = wi < wees.length && wees[wi].at < end + 3 * HOUR ? wees[wi] : undefined;
    while (ti < toilet.length && toilet[ti].at < start) ti++;
    const trips = morning ? ti < toilet.length && toilet[ti].at < morning.at : true;
    if (evening && morning && !trips) mark('quiet-night', morning.at);
  }

  const walks = evs.filter((e): e is LogEvent<'walk'> => e.type === 'walk' && e.data.auto !== 'pending');
  mark('carried-out', walks.find((e) => e.data.carried)?.at);
  mark('first-walk', walks.find((e) => !e.data.carried)?.at);

  mark('settled-alone', aloneSessions(evs, 1000).find((s) => s.settled && s.minutes >= 10)?.end);
  mark('dog-friend', evs.find((e) => e.type === 'social' && DOG_ITEMS.has(e.data.itemId) && e.data.reaction === 'happy')?.at);

  return FIRSTS.filter((f) => found.has(f.id))
    .map((f) => ({ ...f, at: found.get(f.id)! }))
    .sort((a, b) => a.at - b.at);
}

/** "He's home!" counts only if it was tapped on or after the home date (not before a postponed pick-up). */
export function validArrival(homeDate: ISODate | undefined, arrivedAt: Millis | undefined): Millis | undefined {
  return homeDate && arrivedAt !== undefined && arrivedAt >= parseISODate(homeDate).getTime() ? arrivedAt : undefined;
}

/** The next few still to come, in list order (the order they usually happen). */
export function upcomingFirsts(reached: First[], n = 3): FirstDef[] {
  const got = new Set(reached.map((f) => f.id));
  return FIRSTS.filter((f) => !got.has(f.id)).slice(0, n);
}

/**
 * Firsts to cheer on this device: not cheered yet, reached since the device
 * started watching (so a new phone doesn't replay the whole history), and
 * recent (not on a tablet that's been off for a week).
 */
export function freshFirsts(all: First[], seen: Set<string>, since: Millis, now: Millis): First[] {
  return all.filter((f) => !seen.has(f.id) && f.at >= since && now - f.at < 2 * DAY);
}
